import { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { getCashCollected } from "@/integrations/api/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { HandCoins, ChevronDown, ChevronUp, FileUp, Calendar, IndianRupee, Upload } from "lucide-react";
import { useSearchParams } from "react-router-dom";

interface CashCollectedApplication {
  transaction_id: number;
  activity_id?: number | null;
  loan_id: number;
  repayment_id: number;
  applicant_id: string;
  applicant_name: string;
  payment_date: string;
  payment_amount: number;
  cash_in_hand: 1;
}

interface CashCollectedResponse {
  status: string;
  total_applications: number;
  applications: CashCollectedApplication[];
}

interface CashCollectedProps {
  onUpdate: () => void;
  onNavigateToPayment?: (
    loanId: number,
    repaymentId: number,
    paymentAmount: number,
    paymentDate: string,
    applicantId: string,
    transactionId: number,
    activityId: number | null,
    applicantName?: string
  ) => void;
  onRefReady?: (ref: { refresh: () => void }) => void;
  filters?: {
    branch?: string[];
    teamLead?: string[];
    rm?: string[];
    sourceTeamLead?: string[];
    sourceRm?: string[];
    dealer?: string[];
    lender?: string[];
    status?: string[];
    repayment?: string[];
    dpdBucket?: string[];
    specialCaseFilter?: string[];
  };
  searchTerm?: string;
  onUploadMultipleReceipt?: () => void;
}

const CashCollected = ({ onUpdate, onNavigateToPayment, onRefReady, filters, searchTerm, onUploadMultipleReceipt }: CashCollectedProps) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cashCollectedApplications, setCashCollectedApplications] = useState<CashCollectedApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCashCollected = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    // Clear existing data before fetching to prevent stale items
    setCashCollectedApplications([]);
    try {
      const data: CashCollectedResponse = await getCashCollected({
        search: searchTerm?.trim() || undefined,
        branch: filters?.branch || [],
        teamLead: filters?.teamLead || [],
        rm: filters?.rm || [],
        sourceTeamLead: filters?.sourceTeamLead || [],
        sourceRm: filters?.sourceRm || [],
        dealer: filters?.dealer || [],
        lender: filters?.lender || [],
        status: filters?.status || [],
        repayment: filters?.repayment || [],
        dpdBucket: filters?.dpdBucket || [],
        specialCaseFilter: filters?.specialCaseFilter || [],
      });
      console.log('Fetched cash collected applications:', data);
      // Sort by payment_date in descending order (most recent first)
      const sortedApplications = (data.applications || []).sort((a, b) => {
        const dateA = new Date(a.payment_date).getTime();
        const dateB = new Date(b.payment_date).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
      setCashCollectedApplications(sortedApplications);
    } catch (error) {
      console.error('Error fetching cash collected applications:', error);
      toast.error('Failed to fetch cash collected applications');
      // Clear on error to prevent showing stale data
      setCashCollectedApplications([]);
    } finally {
      setLoading(false);
    }
  }, [user, searchTerm, filters]);

  // Fetch data on mount (dashboard opening) and when section is opened
  useEffect(() => {
    if (user) {
      fetchCashCollected();
    }
  }, [user, fetchCashCollected]);

  // Fetch data when section is opened (additional fetch for fresh data)
  useEffect(() => {
    if (isOpen && user) {
      fetchCashCollected();
    }
  }, [isOpen, user, fetchCashCollected]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefReady) {
      onRefReady({
        refresh: () => {
          fetchCashCollected();
        }
      });
    }
  }, [onRefReady, fetchCashCollected]);

  const handleCashSubmitted = (e: React.MouseEvent, application: CashCollectedApplication) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up to CollapsibleTrigger
    
    // Navigate to application with payment tab
    if (onNavigateToPayment) {
      onNavigateToPayment(
        application.loan_id,
        application.repayment_id,
        application.payment_amount,
        application.payment_date,
        application.applicant_id,
        application.transaction_id,
        application.activity_id ?? null,
        application.applicant_name
      );
    } else {
      // Fallback: use search params to navigate
      const newParams = new URLSearchParams(searchParams);
      newParams.set('loan_id', application.loan_id.toString());
      newParams.set('repayment_id', application.repayment_id.toString());
      newParams.set('tab', 'payment');
      setSearchParams(newParams);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch {
      return dateString;
    }
  };

  const applicantInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const formatAmount = (amount: number) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return "₹0";
    return `₹${value.toLocaleString("en-IN")}`;
  };

  const totalAmount = useMemo(
    () => cashCollectedApplications.reduce((sum, application) => sum + (Number(application.payment_amount) || 0), 0),
    [cashCollectedApplications]
  );

  const collectionCount = cashCollectedApplications.length;

  const groupedCases = useMemo(() => {
    const groups = new Map<string, CashCollectedApplication[]>();
    for (const application of cashCollectedApplications) {
      const key = application.applicant_id || String(application.loan_id);
      const existing = groups.get(key);
      if (existing) {
        existing.push(application);
      } else {
        groups.set(key, [application]);
      }
    }

    return Array.from(groups.entries())
      .map(([key, payments]) => {
        const sortedPayments = [...payments].sort(
          (a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
        );
        const latest = sortedPayments[0];
        return {
          key,
          applicantName: latest.applicant_name,
          applicantId: latest.applicant_id,
          payments: sortedPayments,
          collectionCount: sortedPayments.length,
          totalAmount: sortedPayments.reduce((sum, payment) => sum + (Number(payment.payment_amount) || 0), 0),
          latestPaymentTime: new Date(latest.payment_date).getTime(),
        };
      })
      .sort((a, b) => b.latestPaymentTime - a.latestPaymentTime);
  }, [cashCollectedApplications]);

  return (
    <Card className="overflow-hidden border-gray-200 shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          className="flex cursor-pointer flex-col gap-2.5 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-5 sm:py-4"
          onClick={() => setIsOpen((open) => !open)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsOpen((open) => !open);
            }
          }}
        >
          <div className="flex items-center justify-between gap-2 sm:contents">
            <div className="flex min-w-0 items-center gap-2 text-left sm:flex-wrap sm:gap-x-2.5 sm:gap-y-1.5">
              <HandCoins className="h-5 w-5 shrink-0 text-gray-900" />
              <span className="text-base font-semibold text-gray-900">Cash Collected</span>
              {collectionCount > 0 && (
                <span className="hidden items-center gap-2 sm:inline-flex">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    {collectionCount} {collectionCount === 1 ? "Collection" : "Collections"}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                    Total Amount: <span className="ml-1 font-semibold tabular-nums">{formatAmount(totalAmount)}</span>
                  </span>
                </span>
              )}
            </div>
            <span
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 sm:hidden"
              aria-hidden
            >
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </span>
          </div>
          {collectionCount > 0 && (
            <div className="flex flex-nowrap items-center gap-2 sm:hidden">
              <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                {collectionCount} {collectionCount === 1 ? "Collection" : "Collections"}
              </span>
              <span className="inline-flex min-w-0 items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                <span className="truncate">
                  Total Amount: <span className="font-semibold tabular-nums">{formatAmount(totalAmount)}</span>
                </span>
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full gap-1.5 border-gray-300 bg-white text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:w-auto"
              onClick={(event) => {
                event.stopPropagation();
                onUploadMultipleReceipt?.();
              }}
            >
              <FileUp className="h-4 w-4" />
              Upload Receipt for Multiple Cash Deposit
            </Button>
            <span
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 sm:inline-flex"
              aria-hidden
            >
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </span>
          </div>
        </div>

        <CollapsibleContent>
          <CardContent className="space-y-3 px-4 pb-4 pt-0 sm:px-5">
            {loading ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
                <p className="text-sm text-gray-500">Loading cash collected applications...</p>
              </div>
            ) : collectionCount === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <HandCoins className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-base font-medium text-gray-700">No cash collected applications</p>
                <p className="text-sm">All applications with cash in hand will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groupedCases.map((group) => (
                  <div key={group.key} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <div className="flex flex-col gap-2 border-b border-gray-100 bg-gray-50/70 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
                      <div className="flex min-w-0 items-center justify-between gap-2 sm:justify-start sm:gap-2.5">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                            {applicantInitials(group.applicantName)}
                          </span>
                          <span className="truncate text-sm font-semibold text-gray-900">
                            {group.applicantName}
                          </span>
                        </div>
                        <Badge variant="outline" className="shrink-0 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-500 sm:hidden">
                          {group.applicantId}
                        </Badge>
                      </div>
                      <div className="flex flex-nowrap items-center gap-2">
                        <span className="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                          {group.collectionCount} {group.collectionCount === 1 ? "Collection" : "Collections"}
                        </span>
                        <span className="inline-flex min-w-0 items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                          <span className="truncate">
                            Total Amount: <span className="font-semibold tabular-nums">{formatAmount(group.totalAmount)}</span>
                          </span>
                        </span>
                      </div>
                      <Badge variant="outline" className="ml-auto hidden shrink-0 rounded-full bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-500 sm:inline-flex">
                        {group.applicantId}
                      </Badge>
                    </div>
                    <div>
                      {group.payments.map((payment) => (
                          <div
                            key={payment.transaction_id}
                            className="grid grid-cols-1 items-center gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0 sm:grid-cols-3"
                          >
                            <div className="flex items-center justify-between gap-3 sm:contents">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                                <Calendar className="h-4 w-4" />
                              </span>
                              <div className="min-w-0 leading-tight">
                                <p className="text-[11px] font-medium text-gray-400">Payment Date</p>
                                <p className="truncate text-sm font-semibold text-gray-900">{formatDate(payment.payment_date)}</p>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 sm:justify-center">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                                <IndianRupee className="h-4 w-4" />
                              </span>
                              <div className="leading-tight">
                                <p className="text-[11px] font-medium text-gray-400">Payment Amount</p>
                                <p className="text-sm font-semibold tabular-nums text-gray-900">
                                  {formatAmount(payment.payment_amount)}
                                </p>
                              </div>
                            </div>
                            </div>
                            <div className="sm:flex sm:justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleCashSubmitted(e, payment)}
                                className="h-9 w-full justify-center gap-1.5 whitespace-nowrap border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50 sm:w-auto"
                              >
                                <Upload className="h-3.5 w-3.5 text-gray-500" />
                                Cash Submitted - Upload Proof
                              </Button>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default CashCollected;
